import 'regenerator-runtime/runtime';
import { apimCall } from './../helpers/api';
import { showMessage } from '../helpers/MessageUtility';
import { GfReduxDispatcher, RootState } from '../gf-types';
import { TagPayload } from './tags-types';
import { GoFormzServices } from '../AppSettings';

export const TAGS_GET_REQUEST = 'TAGS_GET_REQUEST';
export const TAGS_GET_SUCCESS = 'TAGS_GET_SUCCESS';
export const TAGS_GET_FAILURE = 'TAGS_GET_FAILURE';
export const FORM_TAGS_GET_REQUEST = 'FORM_TAGS_GET_REQUEST';
export const FORM_TAGS_GET_SUCCESS = 'FORM_TAGS_GET_SUCCESS';
export const FORM_TAGS_ADD = 'FORM_TAGS_ADD';
export const FORM_TAGS_REMOVE = 'FORM_TAGS_REMOVE';
export const FORM_TAGS_SAVE_REQUEST = 'FORM_TAGS_SAVE_REQUEST';
export const FORM_TAGS_SAVE_SUCCESS = 'FORM_TAGS_SAVE_SUCCESS';
export const FORM_TAGS_SAVE_FAILURE = 'FORM_TAGS_SAVE_FAILURE';
export const TAGS_ADD_REQUEST = 'TAGS_ADD_REQUEST';
export const TAGS_ADD_SUCCESS = 'TAGS_ADD_SUCCESS';
export const TAGS_ADD_FAILURE = 'TAGS_ADD_FAILURE';
export const TAGS_DELETE_REQUEST = 'TAGS_DELETE_REQUEST';
export const TAGS_DELETE_SUCCESS = 'TAGS_DELETE_SUCCESS';
export const TAGS_DELETE_FAILURE = 'TAGS_DELETE_FAILURE';
export const TAGS_RENAME_REQUEST = 'TAGS_RENAME_REQUEST';
export const TAGS_RENAME_SUCCESS = 'TAGS_RENAME_SUCCESS';
export const TAGS_RENAME_FAILURE = 'TAGS_RENAME_FAILURE';
export const TAGS_MOVE_REQUEST = 'TAGS_MOVE_REQUEST';
export const TAGS_MOVE_SUCCESS = 'TAGS_MOVE_SUCCESS';
export const TAGS_MOVE_FAILURE = 'TAGS_MOVE_FAILURE';

export const getAllNestedTags = () => async (dispatch) => {
  dispatch({
    type: TAGS_GET_REQUEST,
    payload: {
      loading: true
    }
  });
  const response = await apimCall(GoFormzServices.TemplateService, '/tags');

  dispatch({
    type: TAGS_GET_SUCCESS,
    payload: {
      items: response.data,
      loading: false,
      loaded: true
    }
  });
};

export const getFormTags = (formId) => async (dispatch) => {
  dispatch({
    type: FORM_TAGS_GET_REQUEST,
    payload: {
      formTagsLoading: true,
      formTags: null,
      deletedFormTags: null,
      addedFormTags: null
    }
  });
  const response = await apimCall(GoFormzServices.TemplateService, `/forms/${formId}/tags`);

  dispatch({
    type: FORM_TAGS_GET_SUCCESS,
    payload: {
      formTags: response.data,
      formTagsLoading: false,
      formTagsLoaded: true
    }
  });
};

export const addFormTag = (tagId, tagName) => ({
  type: FORM_TAGS_ADD,
  payload: {
    tagId,
    tagName
  }
});

export const removeFormTag = (tagId) => ({
  type: FORM_TAGS_REMOVE,
  payload: {
    tagId
  }
});

export const saveFormTagChanges = (formId, success) => async (dispatch, getState: () => RootState) => {
  dispatch({
    type: FORM_TAGS_SAVE_REQUEST,
    payload: { saving: true }
  });

  try {
    const state: RootState = getState();
    const deletedFormTags = state.tags.deletedFormTags;
    const addedFormTags = state.tags.addedFormTags;
    const formTags = state.tags.formTags;
    const savePromises = [];

    if (deletedFormTags && deletedFormTags.length > 0) {
      savePromises.push(apimCall(GoFormzServices.TemplateService, `/forms/${formId}/tags`, 'DELETE', deletedFormTags));
    }
    if (addedFormTags && addedFormTags.length > 0) {
      savePromises.push(apimCall(GoFormzServices.TemplateService, `/forms/${formId}/tags`, 'POST', addedFormTags));
    }
    if (savePromises.length > 0) {
      await Promise.all(savePromises);
    }

    dispatch({
      type: FORM_TAGS_SAVE_SUCCESS,
      payload: { saving: false }
    });
    success(formTags ? formTags : []);
  } catch (err) {
    console.error(err);
    dispatch({
      type: FORM_TAGS_SAVE_FAILURE,
      payload: { saving: false }
    });
  }
};

export const createTag = (name: string) => async (dispatch, getState: () => RootState) => {
  const state: RootState = getState();
  const existingTags = state.tags.items;
  const matchingTag = existingTags.find((x) => x.name.toLowerCase() === name.toLowerCase());

  if (matchingTag) {
    return;
  } else {
    await createTagInner(name, dispatch);
  }
};

const createTagInner = async (name, dispatch) => {
  try {
    dispatch({
      type: TAGS_ADD_REQUEST,
      payload: { savingTag: true }
    });

    const response = await apimCall(GoFormzServices.TemplateService, '/tags', 'POST', { name });

    dispatch({
      type: TAGS_ADD_SUCCESS,
      payload: {
        savingTag: false,
        id: response.data,
        name
      }
    });
    return { id: response.data, name };
  } catch (err) {
    console.error(err);
    dispatch({
      type: TAGS_ADD_FAILURE,
      payload: { savingTag: false }
    });
    showMessage('Error', 'There was an error adding the tag.');
    return null;
  }
};

export const createAndAddTag = (name: string) => async (dispatch, getState: () => RootState) => {
  const state: RootState = getState();
  const existingTags = state.tags.items;
  const matchingTag = existingTags.find((x) => x.name.toLowerCase() === name.toLowerCase());

  if (matchingTag) {
    dispatch(addFormTag(matchingTag.id, matchingTag.name));
  } else {
    const result = await createTagInner(name, dispatch);
    if (result) {
      dispatch(addFormTag(result.id, result.name));
    }
  }
};

export const deleteTag = (id) => async (dispatch) => {
  try {
    dispatch({
      type: TAGS_DELETE_REQUEST,
      payload: { savingTag: true }
    });

    await apimCall(GoFormzServices.TemplateService, `/tags/${id}`, 'DELETE');

    dispatch({
      type: TAGS_DELETE_SUCCESS,
      payload: {
        savingTag: false,
        id
      }
    });
  } catch (err) {
    console.error(err);
    dispatch({
      type: TAGS_DELETE_FAILURE,
      payload: { savingTag: false }
    });
    showMessage('Error', 'There was an error deleting the tag.');
  }
};

export const renameTag = (id, name, parentId) => async (dispatch) => {
  try {
    dispatch({
      type: TAGS_RENAME_REQUEST,
      payload: { savingTag: true }
    });

    await apimCall(GoFormzServices.TemplateService, `/tags/${id}`, 'PUT', {
      name,
      parentId: parentId || null
    });

    dispatch({
      type: TAGS_RENAME_SUCCESS,
      payload: {
        savingTag: false,
        id,
        name
      }
    });
  } catch (err) {
    console.error(err);
    dispatch({
      type: TAGS_RENAME_FAILURE,
      payload: { savingTag: false }
    });
    showMessage('Error', 'There was an error renaming the tag.');
  }
};

export const moveTag = (id: string, name: string, parentId: string) => async (dispatch: GfReduxDispatcher<Partial<TagPayload>>) => {
  try {
    dispatch({
      type: TAGS_MOVE_REQUEST,
      payload: { savingTag: true }
    });

    await apimCall(GoFormzServices.TemplateService, `/tags/${id}`, 'PUT', {
      name,
      parentId
    });

    dispatch({
      type: TAGS_MOVE_SUCCESS,
      payload: {
        savingTag: false,
        id,
        parentId
      }
    });
  } catch (err) {
    console.error(err);
    dispatch({
      type: TAGS_MOVE_FAILURE,
      payload: { savingTag: false }
    });
    showMessage('Error', 'There was an error moving the tag.');
  }
};
